import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { PnL, fmt, money, PageHeader, Table, Td } from "../../components/dashboard/Shared";
import { PeriodFilter } from "./client/PeriodFilter";

const Block = ({ s, accent }) => (
  <div className="text-xs leading-5">
    <span className="tabular text-white">{s.trades}</span> <span className="text-gray-600">trades</span>
    <div><span className={`tabular ${accent}`}>{fmt(s.gross_profit)}</span> <span className="text-gray-600">/</span> <span className="tabular text-[#FF3B30]">{fmt(s.gross_loss)}</span></div>
    <PnL value={s.net} />
  </div>
);

export default function TradeIntelligence() {
  const [period, setPeriod] = useState({ preset: "week" });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = period.preset === "custom" ? { preset: "custom", from_day: period.from_day, to_day: period.to_day } : { preset: period.preset };
      setData((await api.get("/manager/trade-intelligence", { params })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  return (
    <div data-testid="trade-intelligence-page">
      <PageHeader title="Trade Intelligence" sub="ChartzFx signal trades vs client's own manual trades — per client, per period" />
      <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>
      {!data ? <div className="skeleton h-48" /> : (
        <>
          <p className="text-[11px] text-gray-600 mb-3">Closed trades {data.range.from} → {data.range.to}</p>
          <div className="grid md:grid-cols-3 gap-3 mb-5">
            {[["ChartzFx Signals", t.chartzfx, "text-[#7000FF]", "intel-total-chartzfx"], ["Client Manual", t.manual, "text-[#FFD600]", "intel-total-manual"], ["All Trades", t.total, "text-[#00F0FF]", "intel-total-all"]].map(([l, s, c, id]) => (
              <div key={l} className="card-dark p-5" data-testid={id}>
                <p className={`label-xs ${c}`}>{l}</p>
                <div className="flex items-end justify-between mt-2">
                  <div><p className="tabular text-2xl text-white">{s.trades}</p><p className="text-[10px] text-gray-500">trades</p></div>
                  <div className="text-right text-xs"><p className="text-[#00E676] tabular">+{fmt(s.gross_profit)}</p><p className="text-[#FF3B30] tabular">{fmt(s.gross_loss)}</p><p className="text-sm mt-1"><PnL value={s.net} /></p></div>
                </div>
              </div>
            ))}
          </div>
          <div className="card-dark p-4">
            {!data.clients.length ? <p className="text-sm text-gray-500 py-8 text-center">No clients with trades in this period.</p> : (
              <Table headers={["Client", "MT5", "ChartzFx Signals (P / L / Net)", "Client Manual (P / L / Net)", "Total Net", ""]} testId="intel-table">
                {data.clients.map((r) => (
                  <tr key={r.account_id} data-testid={`intel-row-${r.account_id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <Td className="text-white">{r.client_name}<div className="text-[11px] text-gray-500">{r.client_email}</div></Td>
                    <Td className="text-xs tabular">{r.broker}<div className="text-gray-500">{r.login}</div></Td>
                    <Td><Block s={r.chartzfx} accent="text-[#00E676]" /></Td>
                    <Td><Block s={r.manual} accent="text-[#00E676]" /></Td>
                    <Td className="tabular text-base"><PnL value={r.total.net} /><div className="text-[10px] text-gray-600">{money(r.total.gross_profit)} / {money(r.total.gross_loss)}</div></Td>
                    <Td><Link data-testid={`intel-open-${r.account_id}`} to={`/manager/client/${r.account_id}`} className="text-[11px] text-[#00F0FF] hover:underline">Open client →</Link></Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
